// Copyright (c) Microsoft Corporation. All rights reserved.
// Licensed under the MIT License.
import * as Adaptive from 'adaptivecards';
import DraggableElement from '../DraggableElement';
import DesignerPeerInplaceEditor from './DesignerPeerInplaceEditor';
import CardDesignerSurface from '../CardDesignerSurface';
import DesignerPeerTreeItem from './DesignerPeerTreeItem';
import { Rect, IPoint } from '../Miscellaneous';
import { AllowedChildItems } from '../CardConstants';
import { TargetVersion } from '../Shared';
import { addChildCommand } from './DesignerPeersUtils';
import DesignerPeerRegistrationBase from '../DesignerPeerRegistrationBase';
import PropertySheet from '../PropertyEditor/PropertySheet';
import PropertySheetCategory from '../PropertyEditor/PropertySheetCategory';
import PropertySheetContext from '../PropertyEditor/PropertySheetContext';
import GeneratePropertyFields from '../PropertyEditor/GeneratePropertyFields';

export default abstract class DesignerPeer extends DraggableElement {
    [x: string]: any;
    private _parent: DesignerPeer;
    private _children: Array<DesignerPeer> = [];
    private _isSelected: boolean = false;
    private _inplaceEditorOverlay: HTMLElement;
    private _inplaceEditor: DesignerPeerInplaceEditor = null;

    private closeInplaceEditor(applyChanges: boolean) {
        if (this._inplaceEditor) {
            if (applyChanges) {
                this._inplaceEditor.applyChanges();

                this.changed(true);
            }

            this._inplaceEditor = null;

            this._inplaceEditorOverlay.remove();
        }
    }

    private tryOpenInplaceEditor(): boolean {
        this._inplaceEditor = this.createInplaceEditor();

        if (this._inplaceEditor) {
            this._inplaceEditor.onClose = (applyChanges: boolean) => {
                this.closeInplaceEditor(applyChanges);
            };

            this._inplaceEditorOverlay = document.createElement('div');
            this._inplaceEditorOverlay.tabIndex = 0;
            this._inplaceEditorOverlay.style.zIndex = '600';
            this._inplaceEditorOverlay.style.backgroundColor = 'transparent';
            this._inplaceEditorOverlay.style.position = 'absolute';
            this._inplaceEditorOverlay.style.left = '0';
            this._inplaceEditorOverlay.style.top = '0';
            this._inplaceEditorOverlay.style.width =
                document.documentElement.scrollWidth + 'px';
            this._inplaceEditorOverlay.style.height =
                document.documentElement.scrollHeight + 'px';
            this._inplaceEditorOverlay.onfocus = (e) => {
                this.closeInplaceEditor(true);
            };

            let boundingRect = this.getCardObjectBoundingRect();

            let inplaceEditorHost = document.createElement('div');
            inplaceEditorHost.className = 'acd-inplace-editor-host';
            inplaceEditorHost.style.left =
                Math.floor(boundingRect.left + window.pageXOffset) + 'px';
            inplaceEditorHost.style.top =
                Math.floor(boundingRect.top + window.pageYOffset) + 'px';
            inplaceEditorHost.style.width =
                Math.ceil(boundingRect.width) + 'px';
            inplaceEditorHost.style.height =
                Math.ceil(boundingRect.height) + 'px';

            let renderedInplaceEditor = this._inplaceEditor.render();
            renderedInplaceEditor.classList.add('acd-inplace-editor');
            renderedInplaceEditor.tabIndex = 0;
            renderedInplaceEditor.onblur = (e) => {
                this.closeInplaceEditor(true);
            };

            inplaceEditorHost.appendChild(renderedInplaceEditor);

            this._inplaceEditorOverlay.appendChild(inplaceEditorHost);

            document.body.appendChild(this._inplaceEditorOverlay);

            this._inplaceEditor.initialize();

            return true;
        }

        return false;
    }

    protected click(e: MouseEvent) {
        super.click(e);

        this.isSelected = true;
        e.stopPropagation();
    }

    protected doubleClick(e: MouseEvent) {
        super.doubleClick(e);

        this.tryOpenInplaceEditor();
    }

    protected isContainer(): boolean {
        return false;
    }

    protected getToolTip(): string {
        return null;
    }

    protected addCommands(commands: Array<addChildCommand>) {
        //Do nothing in base implementation
    }

    protected internalRender(): HTMLElement {
        let element = document.createElement('div');
        element.classList.add('acd-peer');

        let toolTip = this.getToolTip();

        if (toolTip) {
            element.title = toolTip;
        }

        if (this.isContainer()) {
            element.classList.add('container');
        }

        element.style.position = 'absolute';

        return element;
    }

    protected internalUpdateCssStyles() {
        if (this.isSelected) {
            this.renderedElement.classList.add('selected');
        } else {
            this.renderedElement.classList.remove('selected');
        }

        if (this.dragging) {
            this.renderedElement.classList.add('dragging');
        } else {
            this.renderedElement.classList.remove('dragging');
        }
    }

    protected peerAdded(newPeer: DesignerPeer) {
        this.changed(false);

        if (this.onPeerAdded) {
            this.onPeerAdded(this, newPeer);
        }
    }

    protected peerRemoved(peer: DesignerPeer) {
        if (this.onPeerRemoved) {
            this.onPeerRemoved(peer);
        }
    }

    protected internalUpdateLayout() {
        if (this.renderedElement) {
            let clientRect = this.getBoundingRect();

            this.renderedElement.style.width = clientRect.width + 'px';
            this.renderedElement.style.height = clientRect.height + 'px';
            this.renderedElement.style.left = clientRect.left + 'px';
            this.renderedElement.style.top = clientRect.top + 'px';
        }
    }

    protected createInplaceEditor(): DesignerPeerInplaceEditor {
        return null;
    }

    protected internalGetTreeItemText(): string {
        return null;
    }

    protected abstract internalRemove(): boolean;

    readonly registration: DesignerPeerRegistrationBase;
    readonly designerSurface: CardDesignerSurface;
    readonly treeItem: DesignerPeerTreeItem;

    onParentChanged: (sender: DesignerPeer) => void;
    onSelectedChanged: (sender: DesignerPeer) => void;
    onChanged: (sender: DesignerPeer, updatePropertySheet: boolean) => void;
    onPeerRemoved: (sender: DesignerPeer) => void;
    onPeerAdded: (sender: DesignerPeer, newPeer: DesignerPeer) => void;

    abstract getCardObject(): Adaptive.CardObject;

    constructor(
        parent: DesignerPeer,
        designerSurface: CardDesignerSurface,
        registration: DesignerPeerRegistrationBase,
    ) {
        super();

        this._parent = parent;

        if (!registration) {
            alert((this as any).constructor.name);
        }

        this.registration = registration;
        this.designerSurface = designerSurface;
        this.treeItem = new DesignerPeerTreeItem(this);
    }

    abstract getBoundingRect(): Rect;
    abstract getCardObjectBoundingRect(): Rect;

    changed(updatePropertySheet: boolean) {
        if (this.onChanged) {
            this.onChanged(this, updatePropertySheet);
        }
    }

    getTreeItemText(): string {
        return this.internalGetTreeItemText();
    }

    populatePropertySheet(
        propertySheet: PropertySheet,
        defaultCategory: string = PropertySheetCategory.DefaultCategory,
    ) {
        // Do nothing in base implementation
    }

    canDrop(peer: DesignerPeer): boolean {
        const type = this.getCardObject().getJsonTypeName();
        const forType = peer.getCardObject().getJsonTypeName();
        return (
            AllowedChildItems.has(type) &&
            AllowedChildItems.get(type).includes(forType)
        );
    }

    canBeRemoved(): boolean {
        return true;
    }

    getPeerToInsert(peer: DesignerPeer) {
        return peer;
    }

    insertItemAfter(peer: DesignerPeer, targetPeer: DesignerPeer): void {}

    insertItemBefore(peer: DesignerPeer, targetPeer: DesignerPeer): void {}

    getItemIndex(): number {
        return null;
    }

    tryRemove(): boolean {
        if (!this.remove(true, false)) {
            return false;
        }
        this.parent.removeChild(this);
        return true;
    }

    tryDrop(peer: DesignerPeer, insertionPoint: IPoint): boolean {
        if (this.canDrop(peer)) {
            let targetChild: DesignerPeer = null;
            let insertAfter: boolean;

            for (var i = 0; i < this.getChildCount(); i++) {
                const child = this.getChildAt(i);
                const rect = this.getChildAt(i).getBoundingRect();

                if (rect.isInside(insertionPoint)) {
                    targetChild = child;

                    insertAfter =
                        insertionPoint.y - rect.top >= rect.height / 2;

                    break;
                }
            }

            if (targetChild !== peer) {
                let myIndex = -1;
                if (peer.parent) {
                    myIndex = peer.parent.getChildIndex(peer);
                    if (!peer.tryRemove()) {
                        return false;
                    }
                }

                let peerToBeInserted: DesignerPeer = this.getPeerToInsert(peer);

                if (!targetChild) {
                    let rect = this.getBoundingRect();

                    insertAfter =
                        insertionPoint.y - rect.top >= rect.height / 2;

                    if (this.getChildCount() > 0 && insertAfter) {
                        this.insertItemAfter(
                            peerToBeInserted,
                            this.getChildAt(this.getChildCount() - 1),
                        );
                    } else {
                        this.insertItemAfter(peerToBeInserted, null);
                    }
                } else {
                    if (insertAfter) {
                        this.insertItemAfter(peerToBeInserted, targetChild);
                    } else {
                        this.insertItemBefore(peerToBeInserted, targetChild);
                    }
                }
                if (peerToBeInserted) {
                    this.insertChild(peerToBeInserted, myIndex);
                }
                this.changed(false);
                this.designerSurface.peerToInsert = peerToBeInserted;
                if (peer !== peerToBeInserted) {
                    this.designerSurface.modifyDraggedPeer(peerToBeInserted);
                }

                return true;
            }
        }

        return false;
    }

    insertChild(peer: DesignerPeer, index: number = -1) {
        if (index === -1) {
            this._children.push(peer);
        } else {
            this._children.splice(index, 0, peer);
        }

        peer.parent = this;

        this.peerAdded(peer);
    }

    removeChild(peer: DesignerPeer) {
        var index = this._children.indexOf(peer);

        if (index >= 0) {
            peer.parent = null;
            this._children.splice(index, 1);
        }
    }

    getChildCount(): number {
        return this._children.length;
    }

    getChildAt(index: number): DesignerPeer {
        return this._children[index];
    }

    getChildIndex(peer: DesignerPeer) {
        return this._children.indexOf(peer);
    }

    getCommands(): Array<addChildCommand> {
        let result: Array<addChildCommand> = [];

        this.addCommands(result);

        return result;
    }

    remove(onlyFromCard: boolean, removeChildren: boolean): boolean {
        if (removeChildren) {
            while (this._children.length > 0) {
                this._children[0].remove(onlyFromCard, removeChildren);
            }
        }

        var result = this.internalRemove();

        if (result && !onlyFromCard) {
            if (this.parent) {
                this.parent.removeChild(this);
            }

            this.removeElementsFromDesignerSurface();

            this.peerRemoved(this);
        }

        return result;
    }

    addElementsToDesignerSurface(
        designerSurface: HTMLElement,
        processChildren: boolean = false,
    ) {
        designerSurface.appendChild(this.renderedElement);

        if (processChildren) {
            for (var i = 0; i < this.getChildCount(); i++) {
                this.getChildAt(i).addElementsToDesignerSurface(
                    designerSurface,
                    processChildren,
                );
            }
        }
    }

    removeElementsFromDesignerSurface(processChildren: boolean = false) {
        this.renderedElement.remove();

        if (processChildren) {
            for (var i = 0; i < this.getChildCount(); i++) {
                this.getChildAt(i).removeElementsFromDesignerSurface(
                    processChildren,
                );
            }
        }
    }

    renderDragVisual(): HTMLElement {
        return null;
    }

    buildPropertySheet(targetVersion: TargetVersion): HTMLElement {
        let propertySheetElement: HTMLElement = document.createElement('div');
        let propertySheet = new PropertySheet();

        this.populatePropertySheet(propertySheet);

        propertySheet.render(
            propertySheetElement,
            new PropertySheetContext(targetVersion, this),
        );

        let actionSet: HTMLElement = document.createElement('div');
        let commands = this.getCommands();
        for (let i = 0; i < commands.length; i++) {
            if (commands[i].name !== '') {
                if (commands[i].subCommands.length !== 0) {
                    let action: HTMLElement =
                        new GeneratePropertyFields().generateActionButton(
                            commands[i],
                        );
                    actionSet.appendChild(action);
                } else {
                    const eventCallback = () => {
                        commands[i].execute();
                    };
                    let action: HTMLElement =
                        new GeneratePropertyFields().generateButton(
                            commands[i].name,
                            eventCallback,
                        );

                    action.setAttribute('aria-label', commands[i].name);
                    actionSet.appendChild(action);
                }
            }
        }
        propertySheetElement.appendChild(actionSet);
        return propertySheetElement;
    }

    get parent(): DesignerPeer {
        return this._parent;
    }

    set parent(value: DesignerPeer) {
        this._parent = value;

        if (this.onParentChanged) {
            this.onParentChanged(this);
        }
    }

    get isSelected(): boolean {
        return this._isSelected;
    }

    set isSelected(value: boolean) {
        if (value !== this._isSelected) {
            this._isSelected = value;

            this.updateLayout();

            if (this.onSelectedChanged) {
                this.onSelectedChanged(this);
            }

            this.treeItem.updateLayout();
        }
    }
}
